import * as THREE from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import bloomVertexShader from './shaders/bloom.vs.glsl';
import bloomFragmentShader from './shaders/bloom.fs.glsl';

import { GammaCorrectionShader } from 'three/examples/jsm/shaders/GammaCorrectionShader.js';
import { FilmPass } from 'three/examples/jsm/postprocessing/FilmPass.js';
import { VignetteShader } from 'three/examples/jsm/shaders/VignetteShader.js';
//import { VerticalBlurShader } from 'three/examples/jsm/shaders/verticalblurshader';

import {FXAAShader} from 'three/examples/jsm/shaders/FXAAShader';


const RENDER_TYPES = ['Scene with Glow', 'Glow only', 'Scene only'];

let guiParams = {
    exposure: 1.6,
    bloomStrength: 1.3,
    bloomThreshold: 0.2855,
    bloomRadius: 1.0,
    renderType: RENDER_TYPES[0],
}


/**
 * Postprocessing > bloom
 */
export default class Bloom {

    
    static BLOOM_LAYER = 31;

    constructor(scene, camera, renderer, options = {}){

        //
        this.bloomPass = null;
        this.renderScene = null;
        this.bloomComposer = null;
        this.finalPass = null;
        this.finalComposer = null;

        //private values
        this._materials = {};
        this._darkMaterial = new THREE.MeshBasicMaterial({color:'black'});
        this.layer = new THREE.Layers();
        this.layer.set(Bloom.BLOOM_LAYER);

        //
        guiParams = {
            ...guiParams,
            ...options,
        }

        //
        this._init(scene, camera, renderer);
    }


    _init = (scene, camera, renderer) => {

        //
        const renderScene = new RenderPass(scene, camera);

        //
        const bloomPass = new UnrealBloomPass(new THREE.Vector2(window.innerWidth, window.innerHeight), 1.5, 0.4, 0.85 );
        bloomPass.threshold = guiParams.bloomThreshold;
        bloomPass.strength = guiParams.bloomStrength;
        bloomPass.radius = guiParams.bloomRadius;

        const bloomComposer = new EffectComposer(renderer);
        bloomComposer.renderToScreen = false;
        bloomComposer.addPass( renderScene );
        bloomComposer.addPass( bloomPass );
        
        const finalPass = new ShaderPass(
            new THREE.ShaderMaterial( {
                uniforms: {
                    baseTexture: { value: null },
                    bloomTexture: { value: bloomComposer.renderTarget2.texture }
                },
                vertexShader: bloomVertexShader,
                fragmentShader: bloomFragmentShader,
                defines: {}
            } ), 'baseTexture'
        );
        finalPass.needsSwap = true;

        //
        const finalComposer = new EffectComposer(renderer);

        finalComposer.addPass(renderScene);
        finalComposer.addPass(finalPass);

        //필름효과!?
        //finalComposer.addPass(new ShaderPass(GammaCorrectionShader));
        //finalComposer.addPass(new FilmPass( 0.0, 0.0, 2048, false));
        //finalComposer.addPass(new ShaderPass(VignetteShader));

        //FXAA
        /*
        this.fxaaPass = new ShaderPass(FXAAShader);
        this.fxaaPass.material.uniforms['resolution'].value.x = 1 / window.innerWidth;
        this.fxaaPass.material.uniforms['resolution'].value.y = 1 / window.innerHeight;

        finalComposer.addPass(finalPass);
        */

        //
        this.bloomPass = bloomPass;
        this.renderScene = renderScene;
        this.bloomComposer = bloomComposer;
        this.finalPass = finalPass;
        this.finalComposer = finalComposer;
    }



    initDebugGUI = (gui) => {

        if(!gui){
            return;
        }
    
        const {bloomComposer, bloomPass} = this;
        const folder = gui.addFolder('Bloom Parameters');
    
        gui.add(guiParams, 'renderType', RENDER_TYPES).onChange(function(value){

            console.log(guiParams);
    
            const type = RENDER_TYPES.findIndex(i => i == guiParams.renderType);

            switch(type) {

                case 0:
                    bloomComposer.renderToScreen = false;
                    break;

                case 1:
                    bloomComposer.renderToScreen = true;
                    break;
            }
        });
    
        folder.add(guiParams, 'bloomThreshold', 0.0, 1.0).onChange(function(value) {
            bloomPass.threshold = Number(value);
        });
    
        folder.add(guiParams, 'bloomStrength', 0.0, 10.0).onChange(function (value) {
            bloomPass.strength = Number(value);
        });
    
        folder.add(guiParams, 'bloomRadius', 0.0, 1.0).step( 0.01 ).onChange(function(value) {
            bloomPass.radius = Number(value);
        });
    }


    render = (renderer, scene, camera, deltaTime) => {

        if(!this.renderScene){
            return;
        }

        /*
        this.renderScene.scene = scene;
        this.renderScene.camera = camera;
        this.bloomComposer.renderer = renderer;
        this.finalComposer.renderer = renderer;
        */
 
        //
        const type = RENDER_TYPES.findIndex(i => i == guiParams.renderType);

        switch(type) {

            case 0:
            default:
                this._renderBloom(camera, scene, deltaTime, true);
                this.finalComposer.render(deltaTime);
                break;

            case 1:
                this._renderBloom(camera, scene, deltaTime, false);
                break;

            case 2:
                renderer.render(scene, camera);
                break;
        }
    }


    _renderBloom = (camera, scene, deltaTime, mask) => {

        const {bloomComposer} = this;

        if(mask === true){
    
            scene.traverse(this._darkenNonBloomed);
            bloomComposer.render(deltaTime);
            scene.traverse(this._restoreMaterial);
    
        }else{
    
            const mask = camera.layers.mask;
            camera.layers.set(Bloom.BLOOM_LAYER);
            bloomComposer.render(deltaTime);
            camera.layers.mask = mask;
        }
    }

    
    /**
     * 블룸 레이어가 아닌 녀석들을 검게 처리
     * @param {*} obj 
     */
    _darkenNonBloomed = (obj) => {

        const {_materials, _darkMaterial, layer} = this;

        if (obj.isMesh && layer.test(obj.layers) === false) {
            _materials[obj.uuid] = obj.material;
            obj.material = _darkMaterial;
        }
    }


    /**
     * 검게 처리했던 머티리얼 복구
     * @param {*} obj 
     */
    _restoreMaterial = (obj) => {

        const {_materials} = this;

        if(_materials[obj.uuid]){
            obj.material = _materials[obj.uuid];
            delete _materials[obj.uuid];
        }
    }


    resize = (width, height, pixelRatio) => {

        this.bloomComposer.setSize(width, height);
        this.finalComposer.setSize(width, height);
        this.bloomComposer.setPixelRatio(pixelRatio);
        this.finalComposer.setPixelRatio(pixelRatio);

        if(this.fxaaPass){
            this.fxaaPass.material.uniforms['resolution'].value.x = 1 / width;
            this.fxaaPass.material.uniforms['resolution'].value.y = 1 / height;
        }
        

        this.bloomPass.resolution.set(width * pixelRatio, height * pixelRatio);
    }
}